import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Button from 'mixtiles-web-common/ui/Button'
import { analytics, EVENT_NAMES } from 'services/Analytics/Analytics'
import type { CartUpsellFlowProps } from '../../cartUpsells.registry'
import { UpsellWallSheet } from '../../components/wall/UpsellWallSheet'
import type { AiWallUpsell, AiWallUpsellId } from '../aiWall.types'
import { getAiWallWall } from '../aiWallTiles'
import { useAddAiWallToBag } from '../hooks/useAddAiWallToBag'

function getAddTilesButtonKey(tilesCount: number): string {
  if (tilesCount === 0) return 'cart_upsells.select_tiles_to_add'
  return tilesCount === 1
    ? 'cart_upsells.add_tiles_one'
    : 'cart_upsells.add_tiles_other'
}

export function AiWallFlow<Id extends AiWallUpsellId>({
  upsell,
  offer,
  isOpen,
  onClose,
  onAddedToBag,
  onExited,
  analyticsProps,
}: CartUpsellFlowProps<Id> & { upsell: AiWallUpsell<Id> }) {
  const { t } = useTranslation()
  const [excludedTileIndexes, setExcludedTileIndexes] = useState<
    ReadonlySet<number>
  >(new Set())
  const addAiWallToBagMutation = useAddAiWallToBag({
    upsell,
    analyticsProps,
    onAdded: onAddedToBag,
  })
  // The bag gets the tiles chosen when Add is tapped, so the wall stays as it was from then on.
  const isAddingToBag =
    addAiWallToBagMutation.isPending || addAiWallToBagMutation.isSuccess
  const onTileToggle = (tileIndex: number) => {
    if (isAddingToBag) return
    const isTurningOn = excludedTileIndexes.has(tileIndex)
    const nextExcludedTileIndexes = new Set(excludedTileIndexes)
    if (isTurningOn) {
      nextExcludedTileIndexes.delete(tileIndex)
    } else {
      nextExcludedTileIndexes.add(tileIndex)
    }
    setExcludedTileIndexes(nextExcludedTileIndexes)
    analytics.track(EVENT_NAMES.CART_UPSELL_TILE_TOGGLED, {
      ...analyticsProps,
      'Tile Index': tileIndex,
      'Is Included': isTurningOn,
      'Included Tiles Count':
        offer.results.length - nextExcludedTileIndexes.size,
    })
  }
  // A wall tile's index is its result's index, so the included tiles are the results to add.
  const includedTileIndexes = offer.results
    .map((_result, resultIndex) => resultIndex)
    .filter((resultIndex) => !excludedTileIndexes.has(resultIndex))
  return (
    <UpsellWallSheet
      upsellId={upsell.id}
      analyticsProps={analyticsProps}
      title={t(upsell.flowTitleKey)}
      wall={getAiWallWall(upsell.tile, offer)}
      excludedTileIndexes={excludedTileIndexes}
      onTileToggle={onTileToggle}
      isOpen={isOpen}
      onClose={onClose}
      onExited={onExited}
      footer={
        <Button
          fullWidth
          loading={addAiWallToBagMutation.isPending}
          // Button's loading only swaps in a spinner; disabling is what stops a second tap, also while the sheet animates out.
          disabled={includedTileIndexes.length === 0 || isAddingToBag}
          onClick={() =>
            addAiWallToBagMutation.mutate({
              offer,
              resultIndexes: includedTileIndexes,
            })
          }
          testId="cart-upsell-add-to-bag"
        >
          {t(getAddTilesButtonKey(includedTileIndexes.length), {
            count: includedTileIndexes.length,
          })}
        </Button>
      }
    />
  )
}
